import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import {
  CampaignContentPreferences,
  ContentAccounts,
  ContentStrategy,
  ContentStrategyType,
  Theme,
  WeekDay,
  WeekDays,
} from 'types'
import { CampaignSourceObject } from 'hooks/queries/workspaces/useSource'
import { ContentCategory } from 'utils/brandPlans/contentCategories'

export type { CampaignSourceObject }

export interface ContentTypeSetting {
  count: number
  weekDays: WeekDays
}

export type ContentTypeSettingsMap = Record<ContentCategory, ContentTypeSetting>

export interface GeneratedTheme extends Theme {
  content_brief?: string
  goal_alignment?: string
  strategic_role?: string
  seasonal_tie_in?: string
  call_to_action?: string
  audience?: string[]
  image_description?: string
  title_alternatives?: string[]
}

export type SourceRole = 'source_material' | 'visual_asset'

export interface CampaignSource {
  source: CampaignSourceObject
  roles: SourceRole[]
  mediaFileId?: number
}

export interface Campaign {
  strategyType: ContentStrategyType
  theme: GeneratedTheme
  target_link: string
  startDate: string
  endDate: string
  length: number
  weekDays: WeekDay[]
  sources: CampaignSource[]
  loading?: boolean
  content_preferences?: CampaignContentPreferences
  content_accounts?: ContentAccounts
}

export enum StateId {
  StrategyAndContextSelection = 'strategyAndContextSelection',
  ContentTypeDefaults = 'contentTypeDefaults',
  ReviewDetails = 'reviewDetails',
  Creating = 'creating',
}

export enum EventId {
  Next = 'NEXT',
  SetContentTypes = 'SET_CONTENT_TYPES',
  ThemeResolved = 'THEME_RESOLVED',
  Back = 'BACK',
  Create = 'CREATE',
  Error = 'ERROR',
  SetCampaignDetails = 'SET_CAMPAIGN_DETAILS',
}

export interface MachineContext {
  strategyType: ContentStrategyType | null
  strategyConfig: ContentStrategy | null
  campaign: Campaign
  contentTypeSettings: ContentTypeSettingsMap | null
  themeDetails: string | null
  transient: {
    abortController: AbortController
  }
}

export type MachineEvent =
  | {
      type: EventId.Next
      strategyType: ContentStrategyType
      strategyConfig: ContentStrategy
      sources: CampaignSource[]
      themeDetails: string | null
    }
  | {
      type: EventId.SetContentTypes
      settings?: ContentTypeSettingsMap
      contentAccounts?: ContentAccounts
    }
  | {
      type: EventId.ThemeResolved
      theme: GeneratedTheme
    }
  | {
      type: EventId.SetCampaignDetails
      campaign: Campaign
    }
  | { type: EventId.Back }
  | { type: EventId.Create; campaign: Campaign }
  | { type: EventId.Error; error: Error }

export type MachineState =
  | { value: StateId.StrategyAndContextSelection; context: MachineContext }
  | { value: StateId.ContentTypeDefaults; context: MachineContext }
  | { value: StateId.ReviewDetails; context: MachineContext }
  | { value: StateId.Creating; context: MachineContext }

export const CATEGORY_PROVIDERS: Record<ContentCategory, ExternalIntegrationEnum[]> = {
  still_image_post: [
    ExternalIntegrationEnum.Instagram,
    ExternalIntegrationEnum.Facebook,
    ExternalIntegrationEnum.LinkedIn,
    ExternalIntegrationEnum.X,
    ExternalIntegrationEnum.GoogleBusinessProfile,
  ],
  carousel_post: [
    ExternalIntegrationEnum.Instagram,
    ExternalIntegrationEnum.Facebook,
    ExternalIntegrationEnum.LinkedIn,
  ],
  feed_video_post: [
    ExternalIntegrationEnum.Instagram,
    ExternalIntegrationEnum.Facebook,
    ExternalIntegrationEnum.LinkedIn,
  ],
  short_form_video: [
    ExternalIntegrationEnum.Instagram,
    ExternalIntegrationEnum.TikTok,
    ExternalIntegrationEnum.YouTube,
  ],
  avatar_video: [ExternalIntegrationEnum.Instagram, ExternalIntegrationEnum.TikTok, ExternalIntegrationEnum.YouTube],
  stories: [ExternalIntegrationEnum.Instagram, ExternalIntegrationEnum.Facebook],
  blog_post: [ExternalIntegrationEnum.WordPress, ExternalIntegrationEnum.Wix],
  newsletter: [ExternalIntegrationEnum.Mailchimp, ExternalIntegrationEnum.GoHighLevel],
}

export interface CategoryRowState {
  count: number
  weekDays: WeekDays
  accountIds: number[]
}
